/* eslint-disable security/detect-object-injection */
/* eslint-disable no-plusplus */
import React, { useState, useEffect } from 'react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { SubmissionService, PolicyService } from 'gw-capability-gateway';
import { MLModalService } from 'ml-portals-services-react';
import { MLProductCodeUtil } from 'ml-portals-util-js';
import { platformMessages } from 'ml-components-platform-react';
import MLRatingFactorPrefillComponent from '../MLRatingFactorPrefillComponent/MLRatingFactorPrefillComponent';
import MLRatingFactorConsumerReportsComponent from '../MLRatingFactorConsumerReportsComponent/MLRatingFactorConsumerReportsComponent';
import MLRatingFactorHomeInspectionComponent from '../MLRatingFactorHomeInspectionComponent/MLRatingFactorHomeInspectionComponent';
import MLRatingFactorPriorCarrierComponent from '../MLRatingFactorPriorCarrierComponent/MLRatingFactorPriorCarrierComponent';
import MLRatingFactorCompanyUseComponent from '../MLRatingFactorCompanyUseComponent/MLRatingFactorCompanyUseComponent';

import metadata from './MLRatingFactorComponent.metadata.json5';
import styles from './MLRatingFactorComponent.module.scss';

const PREFILL_VIEW_NAME = 'prefill';
const CONSUMER_REPORTS_VIEW_NAME = 'consumerReports';
const COMPANY_USE_VIEW_NAME = 'companyUse';
const HOME_INSPECTION_VIEW_NAME = 'homeInspection';
const PRIOR_CARRIER_VIEW_NAME = 'priorCarrier';
const HO_DEFAULT_VIEW = CONSUMER_REPORTS_VIEW_NAME;
const PA_PUP_DEFAULT_VIEW = PREFILL_VIEW_NAME;

const MLRatingFactorComponent = (props) => {
    const { jobOrPolicyNumber, isPolicy, isQuote, state, authHeader, viewModelService, userTypeCode, policies,isModernProduct } = props;
    const {
        showProgressDialog
    } = MLModalService();

    const [productType, setProductType] = useState('');
    const [currentView, setCurrentView] = useState('');
    const [isPersonalAuto, setIsPersonalAuto] = useState(false);
    const [isPersonalUmbrella, setIsPersonalUmbrella] = useState(false);
    const [isHomeOwners, setIsHomeOwners] = useState(false);

    const setProductTypeFromSubmission = () => {
        const submissionRequest = SubmissionService.getSubmissionProductType(jobOrPolicyNumber, authHeader);
        showProgressDialog(platformMessages.loading, submissionRequest);
        submissionRequest.then((product) => {
            setProductType(product);
        }, () => {
            console.error(new Error('Unable to retrieve submission'));
        });
    };

    const setProductTypeFromPolicy = () => {
        const policyRequest = PolicyService.getPolicyProductType(jobOrPolicyNumber, authHeader);
        showProgressDialog(platformMessages.loading, policyRequest);
        policyRequest.then((product) => {
            setProductType(product);
        }, () => {
            console.error(new Error('Unable to retrieve policy'));
        });
    };

    useEffect(() => {
        if (isQuote) {
            setProductTypeFromSubmission();
        } else if (isPolicy) {
            setProductTypeFromPolicy();
        } else {
            console.log('Not valid type');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setIsPersonalAuto(MLProductCodeUtil.isPersonalAuto(productType));
        setIsPersonalUmbrella(MLProductCodeUtil.isPersonalUmbrella(productType));
        setIsHomeOwners(MLProductCodeUtil.isHomeOwners(productType));
    }, [productType]);

    useEffect(() => {
        if (isHomeOwners) {
            setCurrentView(HO_DEFAULT_VIEW);
        } else if (isPersonalAuto || isPersonalUmbrella) {
            setCurrentView(PA_PUP_DEFAULT_VIEW);
        }
    }, [isHomeOwners, isPersonalAuto, isPersonalUmbrella]);

    const isCurrentView = (view) => {
        return currentView === view;
    };
    
    // Add keyboard event listener for tabs
    useEffect(() => {
        const tabList = document.getElementById('ratingFactorButtonsGroup');        

        if(tabList) {
            tabList.onkeydown = (e) => {
                if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                    const tabs = tabList.querySelectorAll(':scope > [role="tab"]');
                    const tabsArray = Array.from(tabs);
                    
                    let tabFocus = tabsArray.findIndex((el) => {                        
                        return document.activeElement.id === el.id;
                    });

                    if (e.key === "ArrowRight") {
                        tabFocus++;
                        
                        // If we're at the end, go to the start
                        if (tabFocus >= tabs.length) {
                            tabFocus = 0;
                        }
                    // Move left
                    } else if (e.key === "ArrowLeft") {
                        tabFocus--;
                        
                        // If we're at the start, move to the end
                        if (tabFocus < 0) {
                            tabFocus = tabs.length - 1;
                        }
                    }

                    tabs[tabFocus].focus();
                }
            }
        }
    });

    const overrideProps = {
        ratingFactorButtonsGroup: {
            'aria-label': 'Risk Analysis',
            role: 'tablist'
        },
        prefillButton: {
            visible: !isHomeOwners,
            className: classNames('ml-btn', styles.button, { active: isCurrentView(PREFILL_VIEW_NAME) }),
            onClick: () => {
                setCurrentView(PREFILL_VIEW_NAME)
            },
            'aria-selected': isCurrentView(PREFILL_VIEW_NAME),
            tabindex: (isCurrentView(PREFILL_VIEW_NAME)) ? '0' : '-1',
            role: 'tab'
        },
        consumerReportsButton: {
            className: classNames('ml-btn', styles.button, { active: isCurrentView(CONSUMER_REPORTS_VIEW_NAME) }),
            onClick: () => {
                setCurrentView(CONSUMER_REPORTS_VIEW_NAME)
            },
            'aria-selected': isCurrentView(CONSUMER_REPORTS_VIEW_NAME),
            tabindex: (isCurrentView(CONSUMER_REPORTS_VIEW_NAME)) ? '0' : '-1',
            role: 'tab'
        },
        homeInspectionButton: {
            visible: isHomeOwners,
            className: classNames('ml-btn', styles.button, { active: isCurrentView(HOME_INSPECTION_VIEW_NAME) }),
            onClick: () => {
                setCurrentView(HOME_INSPECTION_VIEW_NAME)
            },
            'aria-selected': isCurrentView(HOME_INSPECTION_VIEW_NAME),
            tabindex: (isCurrentView(HOME_INSPECTION_VIEW_NAME)) ? '0' : '-1',
            role: 'tab'
        },
        priorCarrierButton: {
            visible: !isHomeOwners,
            className: classNames('ml-btn', styles.button, { active: isCurrentView(PRIOR_CARRIER_VIEW_NAME) }),
            onClick: () => {
                setCurrentView(PRIOR_CARRIER_VIEW_NAME)
            },
            'aria-selected': isCurrentView(PRIOR_CARRIER_VIEW_NAME),
            tabindex: (isCurrentView(PRIOR_CARRIER_VIEW_NAME)) ? '0' : '-1',
            role: 'tab'
        },
        companyUseButton: {
            className: classNames('ml-btn', styles.button, { active: isCurrentView(COMPANY_USE_VIEW_NAME) }),
            onClick: () => {
                setCurrentView(COMPANY_USE_VIEW_NAME)
            },
            'aria-selected': isCurrentView(COMPANY_USE_VIEW_NAME),
            tabindex: (isCurrentView(COMPANY_USE_VIEW_NAME)) ? '0' : '-1',
            role: 'tab'
        },
        prefillComponent: {
            visible: isCurrentView(PREFILL_VIEW_NAME),
            jobOrPolicyNumber,
            isQuote,
            isPolicy,
            isPersonalAuto,
            isPersonalUmbrella,
            styles,
            authHeader,
            viewModelService
        },
        consumerReportsComponent: {
            visible: isCurrentView(CONSUMER_REPORTS_VIEW_NAME),
            jobOrPolicyNumber,
            isQuote,
            isPolicy,
            isPersonalAuto,
            isPersonalUmbrella,
            isHomeOwners,
            styles,
            state,
            authHeader,
            userTypeCode,
            isModernProduct
        },
        homeInspectionComponent: {
            visible: isCurrentView(HOME_INSPECTION_VIEW_NAME),
            jobOrPolicyNumber,
            isQuote,
            isPolicy,
            isHomeOwners,
            styles,
            authHeader
        },
        priorCarrierComponent: {
            visible: isCurrentView(PRIOR_CARRIER_VIEW_NAME),
            jobOrPolicyNumber,
            isQuote,
            isPolicy,
            isPersonalAuto,
            isPersonalUmbrella,
            styles,
            authHeader
        },
        companyUseComponent: {
            visible: isCurrentView(COMPANY_USE_VIEW_NAME),
            jobOrPolicyNumber,
            isQuote,
            isPolicy,
            isPersonalAuto,
            isPersonalUmbrella,
            isHomeOwners,
            styles,
            authHeader,
            viewModelService,
            isModernProduct
        }
    }

    const resolvers = {
        resolveComponentMap: {
            MLRatingFactorPrefillComponent,
            MLRatingFactorConsumerReportsComponent,
            MLRatingFactorHomeInspectionComponent,
            MLRatingFactorPriorCarrierComponent,
            MLRatingFactorCompanyUseComponent
        },
        resolveClassNameMap: styles
    }
   
    return (
        <ViewModelForm 
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    )
};

export default MLRatingFactorComponent;

MLRatingFactorComponent.propTypes = {
    jobOrPolicyNumber: PropTypes.string.isRequired,
    isPolicy: PropTypes.bool,
    isQuote: PropTypes.bool
};

MLRatingFactorComponent.defaultProps = {
    isPolicy: false,
    isQuote: false
}